import { siteApiSlice } from "../../app/siteApiSlice";
import { SelectListItem } from "../../components/input/SelectList";
import { DataTablesQueryModel, DataTablesViewModel } from "../../interfaces/dataTables";
import { CoursePromptReviewGroupsViewModel } from "../peerReviewGroups/coursePromptReviewGroups/coursePromptReviewGroupsApiSlice";
import { StimulusMaterialViewModel } from "../stimulusMaterial/stimulusMaterialApiSlice";

export enum PromptType {
	System = "Pre-packaged",
	District = "District",
	School = "School",
	Teacher = "Teacher",
}

export interface ManagePromptsViewModel {
	contentAreas: string[];
	canSeePlagiarismChecker: boolean;
	inactivePrompts: SelectListItem[];
}

export interface PromptListQueryModel extends DataTablesQueryModel {
	genre_Filter: string;
	status_Filter: string;
	grade_Filter: string;
	content_Filter: string;
	level_Filter: string;
	search_Filter: string;
	plagiarism_Filter: string;
}

export interface PromptListViewModel {
	promptId: number;
	title: string;
	description: string;
	genreName: string;
	enabled: boolean;
	statusDate: Date;
	stimulusMaterial: StimulusMaterialViewModel[];
	promptType: PromptType; //teacher, school, pre-packaged, district
	contentAreas: string[];
	isShared: boolean;
	canEdit: boolean;
	coursePromptAdvancedSettings: AdvancedSettingsPromptListViewModel;
	coursePromptPeerReviewSettings: PeerReviewSettingsViewModel;
}

export interface PromptInfoBaseViewModel {
	isNew: boolean;
	canEdit: boolean;
	enabled?: boolean;
	statusDate?: string;
	name: string;
	text: string;
	genreId: number;
	contentAreaIds: number[];
	contentAreas?: string[];
	peerReviewSettings?: PeerReviewSettingsViewModel;
	isShared: boolean;
	wasAlreadyShared: boolean;
}

export interface SavePromptViewModel extends PromptInfoBaseViewModel {
	advancedSettings?: AdvancedSettingsViewModel;
	selectedPromptEnabledInCoursesList: string[];
}

export interface GetPromptViewModel extends PromptInfoBaseViewModel {
	availableContentAreas: SelectListItem[];
	essayCategories: SelectListItem[];
	stimulusMaterials?: StimulusMaterialViewModel[];
	promptEnabledInCoursesList: PromptEnabledInCoursesViewModel[];
	advancedSettings: GetAdvancedSettingsViewModel;
}

export interface AdvancedSettingsPromptListViewModel extends AdvancedSettingsViewModel {
	assignedToNames: string[];
	selectedOrganizerName: string;
}

export interface GetAdvancedSettingsViewModel extends AdvancedSettingsViewModel {
	availableUsers: SelectListItem[];
	availableReminders: SelectListItem[];
	organizersByGenre: SelectListItem[];
}

export interface AdvancedSettingsViewModel {
	assignedToIds: number[];
	dueDateEnabled: boolean;
	dueDate: string;
	dueDateRequired: boolean;
	selectedReminders: number[];
	timerEnabled: boolean;
	timeAllowed: number;
	retriesEnabled: boolean;
	retriesAllowed: number;
	titleRequired: boolean;
	requireSources: boolean;
	finalizeEnabled: boolean;
	finalizeDraftNumber: number;
	scoreDraftNumber: number;
	grammarCheckDraftThreshold: number;
	specializedVocabularyList: string[];
	allowCopyPaste: boolean;
	isOrganizerRequired: boolean;
	selectedOrganizerId?: number;
	canSetPlagiarismSettings?: boolean;
	plagiarismCheckerEnabled?: boolean;
	plagiarismCheckerAllDrafts?: boolean;
	plagiarismCheckedDrafts?: string;
}

export interface PeerReviewSettingsViewModel {
	allowReviews: boolean;
	anonymousAuthor: boolean;
	anonymousReviewers: boolean;
	allowSentenceStarters: boolean;
	groups: CoursePromptReviewGroupsViewModel;
	generalReviewQuestions: PeerReviewQuestionSettingViewModel[];
	reviewQuestions: PeerReviewQuestionSettingViewModel[];
	customReviewQuestions: PeerReviewCustomQuestionSettingViewModel[];
}

export interface PeerReviewCustomQuestionSettingViewModel {
	question: string;
	isEnabled: boolean;
}

export interface PeerReviewQuestionSettingViewModel {
	id: number;
	question: string;
	isEnabled: boolean;
	genreId: number;
}

export interface PromptEnabledInCoursesViewModel {
	value: string;
	text: string;
	selected: boolean;
	isCreatedInCourse: boolean;
}

export interface EmailPromptBaseViewModel {
	promptIds: number[];
	includesDisabledPrompts: boolean;
}

export interface EmailPromptListViewModel extends EmailPromptBaseViewModel {
	courseId: number | null;
}

export interface EmailPromptViewModel extends EmailPromptBaseViewModel {
	proctors: SelectListItem[];
	proctorIds: number[];
}

export const promptApiSlice = siteApiSlice.injectEndpoints({
	endpoints: (build) => ({
		managePrompts: build.query<ManagePromptsViewModel, void>({
			query: () => ({
				url: `/Prompt/ManageSpa`,
			}),
			providesTags: ["CurrentCourse", "Prompts"],
		}),
		getPromptList: build.query<DataTablesViewModel<PromptListViewModel>, PromptListQueryModel>({
			query: (q) => {
				const {
					iSortCol_0,
					sSortDir_0,
					iDisplayStart,
					iDisplayLength,
					genre_Filter,
					status_Filter,
					grade_Filter,
					content_Filter,
					level_Filter,
					search_Filter,
					plagiarism_Filter,
				} = q;
				return {
					url: `/Prompt/AjaxHandler`,
					params: {
						iSortCol_0,
						sSortDir_0,
						iDisplayStart,
						iDisplayLength,
						genre_Filter,
						status_Filter,
						grade_Filter,
						content_Filter,
						level_Filter,
						search_Filter,
						plagiarism_Filter,
					},
				};
			},
			providesTags: ["Prompts", "CurrentCourse"],
		}),
		enablePrompt: build.mutation<void, number[]>({
			query: (payload) => {
				return {
					url: `/Prompt/Enable`,
					method: "POST",
					body: payload,
				};
			},
			invalidatesTags: ["Prompts"],
		}),
		disablePrompt: build.mutation<void, number[]>({
			query: (payload) => {
				return {
					url: `/Prompt/Disable`,
					method: "POST",
					body: payload,
				};
			},
			invalidatesTags: ["Prompts"],
		}),
		deleteCoursePrompt: build.mutation<void, number>({
			query: (id) => {
				return {
					url: `/CoursePrompts/Remove`,
					method: "POST",
					body: { id },
				};
			},
			invalidatesTags: ["Prompts"],
		}),
		reactivateCoursePrompt: build.mutation<void, number>({
			query: (promptId) => {
				return {
					url: `/CoursePrompts/Reactivate`,
					method: "POST",
					body: { promptId },
				};
			},
			invalidatesTags: ["Prompts"],
		}),
		addPromptDefaults: build.query<GetPromptViewModel, void>({
			query: () => ({
				url: `/CoursePrompts/Add`,
			}),
			providesTags: ["CurrentCourse", "Prompts"],
		}),
		addPrompt: build.mutation<number, SavePromptViewModel>({
			query: (payload) => {
				return {
					url: `/CoursePrompts/Add`,
					method: "POST",
					body: payload,
				};
			},
			invalidatesTags: ["Prompts", "WritingHistory"],
		}),
		editPromptInfo: build.query<GetPromptViewModel, number>({
			query: (id) => ({
				url: `/CoursePrompts/Edit`,
				params: { id },
			}),
			providesTags: ["CurrentCourse", "Prompts"],
		}),
		editPrompt: build.mutation<string[], { editPromptId: number; model: SavePromptViewModel }>({
			query: (payload) => {
				return {
					url: `/CoursePrompts/Edit`,
					method: "POST",
					body: payload,
				};
			},
			invalidatesTags: ["Prompts", "WritingHistory"],
		}),
		emailList: build.query<EmailPromptViewModel, EmailPromptListViewModel>({
			query: (q) => {
				const { promptIds, includesDisabledPrompts, courseId } = q;
				return {
					url: `/CoursePrompts/ShareAndEmail`,
					params: { promptIds, includesDisabledPrompts, courseId },
				};
			},
			providesTags: ["CurrentCourse"],
		}),
		sendNotifyEmail: build.mutation<void, EmailPromptViewModel>({
			query: (payload) => {
				return {
					url: `/CoursePrompts/Email`,
					method: "POST",
					body: payload,
				};
			},
			invalidatesTags: ["Prompts"],
		}),
	}),
});

export const {
	useManagePromptsQuery,
	useGetPromptListQuery,
	useEnablePromptMutation,
	useDisablePromptMutation,
	useDeleteCoursePromptMutation,
	useReactivateCoursePromptMutation,
	useLazyAddPromptDefaultsQuery,
	useAddPromptMutation,
	useLazyEditPromptInfoQuery,
	useEditPromptMutation,
	useLazyEmailListQuery,
	useSendNotifyEmailMutation,
} = promptApiSlice;
